import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getLocations, createLocation, updateLocation, deleteLocation } from '../api';

function Locations() {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    location_type: 'regular',
    price_per_car: '',
    paid_car_cost: '',
    has_reservations: false,
    tracks_paid_cars: true
  });
  // Get tenant_id from logged-in user
const user = JSON.parse(localStorage.getItem('user') || '{}');
const tenantId = user.tenant_id || 1;

  const fetchLocations = async () => {
    try {
      const response = await getLocations(tenantId);
      setLocations(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to load locations');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLocations();
  }, []);

  const resetForm = () => {
    setFormData({
      name: '',
      location_type: 'regular',
      price_per_car: '',
      paid_car_cost: '',
      has_reservations: false,
      tracks_paid_cars: true
    });
    setEditingLocation(null);
    setShowForm(false);
  };

  const handleEdit = (location) => {
    setFormData({
      name: location.name,
      location_type: location.location_type,
      price_per_car: location.price_per_car || '',
      paid_car_cost: location.paid_car_cost || '',
      has_reservations: location.has_reservations,
      tracks_paid_cars: location.tracks_paid_cars
    });
    setEditingLocation(location);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        tenant_id: tenantId,
        price_per_car: parseFloat(formData.price_per_car) || 0,
        paid_car_cost: parseFloat(formData.paid_car_cost) || 0
      };

      if (editingLocation) {
        await updateLocation(editingLocation.id, data);
      } else {
        await createLocation(data);
      }
      
      fetchLocations();
      resetForm();
    } catch (err) {
      alert('Failed to save location');
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to deactivate this location?')) {
      try {
        await deleteLocation(id);
        fetchLocations();
      } catch (err) {
        alert('Failed to delete location');
        console.error(err);
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <p className="text-gray-600">Loading locations...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <Link to="/" className="text-blue-500 hover:text-blue-700 mb-2 inline-block">&larr; Back to Home</Link>
            <h1 className="text-3xl font-bold text-gray-900">Locations</h1>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            Add Location
          </button>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {/* Form Modal */}
        {showForm && (
          <div className="fixed inset-0 flex items-center justify-center z-50" style={{backgroundColor: 'rgba(0, 0, 0, 0.75)'}}>
            <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md" style={{backgroundColor: '#ffffff', opacity: 1}}>
              <h2 className="text-xl font-semibold mb-4">
                {editingLocation ? 'Edit Location' : 'Add Location'}
              </h2>
              <form onSubmit={handleSubmit}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                    <select
                      value={formData.location_type}
                      onChange={(e) => setFormData({ ...formData, location_type: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="regular">Regular</option>
                      <option value="tiered">Tiered (Time-based)</option>
                      <option value="flat_rate">Flat Rate Only</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Price Per Car ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.price_per_car}
                      onChange={(e) => setFormData({ ...formData, price_per_car: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Paid Car Cost ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.paid_car_cost}
                      onChange={(e) => setFormData({ ...formData, paid_car_cost: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">Cost to park in paid lots (e.g., Kelly garage)</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.has_reservations}
                        onChange={(e) => setFormData({ ...formData, has_reservations: e.target.checked })}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700">Tracks Reservations</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.tracks_paid_cars}
                        onChange={(e) => setFormData({ ...formData, tracks_paid_cars: e.target.checked })}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700">Tracks Paid Cars</span>
                    </label>
                  </div>
                </div>
                <div className="flex justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                  >
                    {editingLocation ? 'Save Changes' : 'Add Location'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Locations List */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {locations.filter(l => l.active).map((location) => (
            <div key={location.id} className="bg-white rounded-lg shadow p-4">
              <div className="flex justify-between items-start mb-2">
                <h3 className="font-semibold text-gray-900">{location.name}</h3>
                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                  {location.location_type}
                </span>
              </div>
              <div className="text-sm text-gray-600 space-y-1">
                <p>Price per car: ${parseFloat(location.price_per_car || 0).toFixed(2)}</p>
                {location.paid_car_cost > 0 && (
                  <p>Paid car cost: ${parseFloat(location.paid_car_cost).toFixed(2)}</p>
                )}
                <div className="flex gap-3 text-xs">
                  {location.has_reservations && <span className="text-blue-600">Reservations</span>}
                  {location.tracks_paid_cars && <span className="text-green-600">Paid Cars</span>}
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <button
                  onClick={() => handleEdit(location)}
                  className="text-blue-500 hover:text-blue-700 text-sm"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(location.id)}
                  className="text-red-500 hover:text-red-700 text-sm"
                >
                  Deactivate
                </button>
              </div>
            </div>
          ))}
        </div>

        {locations.filter(l => l.active).length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">No locations yet. Add your first location to get started.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default Locations;